import React, { useState, useEffect } from "react";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import Layouts from "../../layouts/layouts";
import DataTable_Component from "../../components/vehiclechallan/Data Table/index";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";

export default function VehicleChallan() {
  const title = "Vehicle Challan Details";

  const [challanData, setChallanData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dataFilter, setDataFilter] = useState("");
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [hubs, setHubs] = useState([]);
  const [selectedHubIds, setSelectedHubIds] = useState([]);
  const [summary, setSummary] = useState({
    totalVehicles: 0,
    totalChallans: 0,
    totalPendingChallans: 0,
    totalPaidChallans: 0,
    totalChallanAmount: 0,
    totalPendingChallanAmount: 0,
    totalPaidChallanAmount: 0,
  });
  const [drivers, setDrivers] = useState([]);
  const [editingDriver, setEditingDriver] = useState(null);
  const [selectedActualDriverId, setSelectedActualDriverId] = useState("");
  const [savingActualDriver, setSavingActualDriver] = useState(false);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const token = localStorage.getItem("token");

        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v1/drivers`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const driverList = response.data?.drivers || [];

        // Only show active drivers because the backend rejects inactive ones.
        setDrivers(driverList.filter((driver) => driver.status === "Active"));
      } catch (error) {
        console.error("Error fetching drivers:", error.response?.data || error);
      }
    };

    fetchDrivers();
  }, []);

  const saveActualDriver = async () => {
    if (!editingDriver || !selectedActualDriverId) {
      alert("Please select a driver.");
      return;
    }

    try {
      setSavingActualDriver(true);

      const token = localStorage.getItem("token");

      await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/api/challan/vehicle/${editingDriver.vehicleId}/challan/${encodeURIComponent(editingDriver.challanNumber)}/actual-driver`,
        { driverId: selectedActualDriverId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/challan/vehicle/${editingDriver.vehicleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setSelectedChallan(response.data.data);
      setEditingDriver(null);
      setSelectedActualDriverId("");

      alert("Actual driver updated successfully.");
    } catch (error) {
      console.error("Error updating actual driver:", error);

      alert(error.response?.data?.message || "Failed to update actual driver.");
    } finally {
      setSavingActualDriver(false);
    }
  };
  const userRole = localStorage.getItem("userRole");

  const formatHub = (hub) => {
    const hubs = Array.isArray(hub) ? hub : hub ? [hub] : [];

    const normalizedHubs = hubs
      .map((name) => String(name || "").trim())
      .filter(Boolean);

    return normalizedHubs.length ? normalizedHubs.join(", ") : "-";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const viewChallan = async (row) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/challan/vehicle/${row.vehicleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setSelectedChallan(response.data.data);
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching challan details:", error);
    }
  };

  const columns = [
    {
      name: <b>Number Plate</b>,
      selector: (row) => row.numberplate,
      sortable: true,
    },

    {
      name: <b>Hub</b>,
      selector: (row) => formatHub(row.hub),
      sortable: true,
      cell: (row) => <span>{formatHub(row.hub)}</span>,
    },

    {
      name: <b>Total Challans</b>,
      selector: (row) => row.totalChallans,
      sortable: true,
      cell: (row) => (
        <span className="badge bg-primary fs-6 px-3 py-2">
          {row.totalChallans}
        </span>
      ),
    },
    {
      name: <b>Pending</b>,
      selector: (row) => row.totalPending,
      sortable: true,
      cell: (row) => (
        <span className="badge bg-danger fs-6 px-3 py-2">
          {row.totalPending}
        </span>
      ),
    },
    {
      name: <b>Paid</b>,
      selector: (row) => row.totalPaid,
      sortable: true,
      cell: (row) => (
        <span className="badge bg-success fs-6 px-3 py-2">{row.totalPaid}</span>
      ),
    },

    {
      name: <b>Total Amount</b>,
      selector: (row) => row.totalAmount,
      sortable: true,
      cell: (row) => (
        <span className="badge bg-primary fs-6 px-3 py-2">
          {row.totalAmount}
        </span>
      ),
    },
    {
      name: <b>Pending Amount</b>,
      selector: (row) => row.pendingAmount,
      sortable: true,
      cell: (row) => (
        <span className="badge bg-danger fs-6 px-3 py-2">
          {row.pendingAmount}
        </span>
      ),
    },
    {
      name: <b>Paid Amount</b>,
      selector: (row) => row.paidAmount,
      sortable: true,
      cell: (row) => (
        <span className="badge bg-success fs-6 px-3 py-2">
          {row.paidAmount}
        </span>
      ),
    },
    {
      name: <b>Latest Challan Status</b>,
      selector: (row) => row.latestChallan?.challanStatus || "-",
      sortable: true,
    },
    {
      name: <b>Latest Challan Date</b>,
      selector: (row) => formatDate(row.latestChallan?.challanDate),
      sortable: true,
    },

    {
      name: <b>Amount</b>,
      selector: (row) =>
        row.latestChallan?.amount ? `₹${row.latestChallan.amount}` : "-",
      sortable: true,
      cell: (row) =>
        row.latestChallan?.amount ? (
          <span
            className="badge fs-6 px-3 py-2"
            style={{
              backgroundColor: "#fff3cd",
              color: "#856404",
              border: "1px solid #ffeeba",
              fontWeight: "bold",
            }}
          >
            {row.latestChallan.amount}
          </span>
        ) : (
          "-"
        ),
    },
    {
      name: <b>Action</b>,
      cell: (row) => (
        <button
          onClick={() => viewChallan(row)}
          className="btn btn-primary btn-sm"
        >
          <FontAwesomeIcon icon={faEye} />
        </button>
      ),
    },
  ];

  const fetchData = async (page = 1, limit = 5) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) return;

      let query = `?page=${page}&limit=${limit}`;

      if (dataFilter) {
        query += `&search=${encodeURIComponent(dataFilter)}`;
      }
      if (
        userRole?.toLowerCase() === "superadmin" &&
        selectedHubIds.length > 0
      ) {
        query += `&hubIds=${selectedHubIds.join(",")}`;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/challan${query}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setChallanData(response.data.data || []);
      setTotalRecords(response.data.pagination?.totalRecords || 0);
      setSummary(
        response.data.summary || {
          totalChallans: 0,
          totalChallanAmount: 0,
          totalPendingChallans: 0,
          totalPendingChallanAmount: 0,
        },
      );
    } catch (error) {
      console.error("Error fetching challans:", error);
    }
  };

  useEffect(() => {
    fetchData(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage, dataFilter, selectedHubIds]);

  const exportToExcel = async () => {
    try {
      const token = localStorage.getItem("token");

      const params = new URLSearchParams();

      if (dataFilter) {
        params.append("search", dataFilter);
      }

      if (
        userRole?.toLowerCase() === "superadmin" &&
        selectedHubIds.length > 0
      ) {
        params.append("hubIds", selectedHubIds.join(","));
      }

      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/challan${query}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const allData = response.data.data || [];

      const excelData = [];

      allData.forEach((vehicle) => {
        if (vehicle.challans && vehicle.challans.length > 0) {
          vehicle.challans.forEach((challan) => {
            excelData.push({
              "Vehicle Number": vehicle.numberplate,

              Hub: formatHub(vehicle.hub),

              "Total Challans": vehicle.totalChallans,
              Pending: vehicle.totalPending,
              Paid: vehicle.totalPaid,

              "Challan Number": challan.challanNumber,
              Status: challan.challanStatus,
              Amount: challan.amount,
              Date: formatDate(challan.challanDate),
              Time: challan.challanTime,
              State: challan.state,
              RTO: challan.rto,
              "Court Status": challan.courtStatus,
              Location: challan.challanPlace,
              Offence: challan.offenseDetails,
              "Payment URL": challan.paymentUrl,
              // Driver Details
              "Primary Driver":
                challan.driverAssignment?.primaryDriver?.driverName || "-",
              "Primary Driver Employee ID":
                challan.driverAssignment?.primaryDriver?.employeeId || "-",

              "Primary Driver Phone":
                challan.driverAssignment?.primaryDriver?.phoneNumber || "-",

              "All Drivers": challan.driverAssignment?.allDrivers?.length
                ? challan.driverAssignment.allDrivers
                    .map(
                      (driver) =>
                        `${driver.driverName} (Employee ID: ${
                          driver.employeeId || "-"
                        }, Phone: ${driver.phoneNumber || "-"})`,
                    )
                    .join(", ")
                : "-",
            });
          });
        } else {
          // Vehicle with no challans
          excelData.push({
            "Vehicle Number": vehicle.numberplate,
            "Total Challans": vehicle.totalChallans,
            Pending: vehicle.totalPending,
            Paid: vehicle.totalPaid,
            "Challan Number": "-",
            Status: "-",
            Amount: "-",
            Date: "-",
            Time: "-",
            State: "-",
            RTO: "-",
            "Court Status": "-",
            Location: "-",
            Offence: "-",
            "Payment URL": "-",
            "Primary Driver": "-",
            "Primary Driver Phone": "-",
            "All Drivers": "-",
          });
        }
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Vehicle Challans");

      XLSX.writeFile(
        workbook,
        `Vehicle_Challans_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
    } catch (error) {
      console.error("Error exporting challans:", error);
    }
  };

  const exportVehicleChallanToExcel = () => {
    if (!selectedChallan?.challans?.length) return;

    const excelData = selectedChallan.challans.map((challan) => ({
      "Vehicle Number": selectedChallan.numberplate,
      "Chassis Number": selectedChallan.chassisnumber,
      "Challan Number": challan.challanNumber,
      Amount: challan.amount,
      Status: challan.challanStatus,
      Date: formatDate(challan.challanDate),
      Time: challan.challanTime,
      State: challan.state,
      RTO: challan.rto,
      "Court Status": challan.courtStatus,
      Location: challan.challanPlace,
      Offence: challan.offenseDetails,
      "Payment URL": challan.paymentUrl,
      "Primary Driver":
        challan.driverAssignment?.primaryDriver?.driverName || "-",
      "Primary Driver Employee ID":
        challan.driverAssignment?.primaryDriver?.employeeId || "-",
      "Primary Driver Phone":
        challan.driverAssignment?.primaryDriver?.phoneNumber || "-",

      "All Drivers": challan.driverAssignment?.allDrivers?.length
        ? challan.driverAssignment.allDrivers
            .map((driver) => `${driver.driverName} (${driver.phoneNumber})`)
            .join(", ")
        : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      selectedChallan.numberplate,
    );

    XLSX.writeFile(
      workbook,
      `${selectedChallan.numberplate}_Challan_Details.xlsx`,
    );
  };

  const totalAmount =
    selectedChallan?.challans?.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0,
    ) || 0;

  const pendingAmount =
    selectedChallan?.challans
      ?.filter((item) => item.challanStatus === "pending")
      ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

  const paidAmount =
    selectedChallan?.challans
      ?.filter((item) => item.challanStatus !== "pending")
      ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

  return (
    <>
      <Layouts />

      <div className="container-fluid">
        <div className="row my-3">
          <div className="col-12">
            <div className="card" style={{ borderTop: "2px solid #4723d9" }}>
              <div className="card-header d-flex justify-content-between border-bottom pb-1">
                <h4>{title}</h4>

                <button
                  onClick={exportToExcel}
                  className="btn btn-success btn-sm"
                >
                  Export to Excel
                </button>
              </div>

              <div className="card-body">
                <DataTable_Component
                  searchBy="numberplate"
                  title_btn={true}
                  apidata={challanData}
                  columns={columns}
                  totalRows={totalRecords}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  dataFilter={dataFilter}
                  setDataFilter={setDataFilter}
                  totalChallans={summary.totalChallans}
                  totalChallanAmount={summary.totalChallanAmount}
                  totalPendingChallans={summary.totalPendingChallans}
                  totalPendingChallanAmount={summary.totalPendingChallanAmount}
                  selectedHubIds={selectedHubIds}
                  setSelectedHubIds={setSelectedHubIds}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <div className="d-flex justify-content-between align-items-center w-100">
            <Modal.Title>
              Challan Details - {selectedChallan?.numberplate}
            </Modal.Title>

            <button
              onClick={exportVehicleChallanToExcel}
              className="btn btn-primary shadow-sm fw-bold px-3"
            >
              <i className="fas fa-file-excel me-2"></i>
              Export to Excel
            </button>
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div
                className="card border-0 shadow"
                style={{
                  background: "linear-gradient(135deg, #0d6efd, #4f9bff)",
                  color: "#fff",
                  borderRadius: "12px",
                }}
              >
                <div className="card-body text-center py-4">
                  <h6 className="text-white-50">Total Challans</h6>
                  <h1 className="fw-bold mb-0">
                    {selectedChallan?.totalChallans}
                  </h1>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div
                className="card border-0 shadow"
                style={{
                  background: "linear-gradient(135deg, #dc3545, #ff6b7a)",
                  color: "#fff",
                  borderRadius: "12px",
                }}
              >
                <div className="card-body text-center py-4">
                  <h6 className="text-white-50">Pending Challans</h6>
                  <h1 className="fw-bold mb-0">
                    {selectedChallan?.totalPending}
                  </h1>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div
                className="card border-0 shadow"
                style={{
                  background: "linear-gradient(135deg, #198754, #39c878)",
                  color: "#fff",
                  borderRadius: "12px",
                }}
              >
                <div className="card-body text-center py-4">
                  <h6 className="text-white-50">Paid Challans</h6>
                  <h1 className="fw-bold mb-0">{selectedChallan?.totalPaid}</h1>
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-4">
              <div
                className="card shadow-sm border-0"
                style={{
                  borderLeft: "5px solid #ffc107",
                  background: "#fffdf5",
                }}
              >
                <div className="card-body text-center">
                  <h6>Total Amount</h6>
                  <h2 className="fw-bold text-warning">
                    ₹{totalAmount.toLocaleString("en-IN")}
                  </h2>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div
                className="card shadow-sm border-0"
                style={{
                  borderLeft: "5px solid #dc3545",
                  background: "#fff5f5",
                }}
              >
                <div className="card-body text-center">
                  <h6>Pending Amount</h6>
                  <h2 className="fw-bold text-danger">
                    ₹{pendingAmount.toLocaleString("en-IN")}
                  </h2>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div
                className="card shadow-sm border-0"
                style={{
                  borderLeft: "5px solid #198754",
                  background: "#f4fff8",
                }}
              >
                <div className="card-body text-center">
                  <h6>Paid Amount</h6>
                  <h2 className="fw-bold text-success">
                    ₹{paidAmount.toLocaleString("en-IN")}
                  </h2>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle Information */}

          <div className="card shadow-sm mb-4">
            <div className="card-header bg-light">
              <strong>Vehicle Information</strong>
            </div>

            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <label className="fw-bold">Number Plate</label>
                  <p>{selectedChallan?.numberplate}</p>
                </div>

                <div className="col-md-4">
                  <label className="fw-bold">Hub</label>
                  <p>
                    {Array.isArray(selectedChallan?.hub)
                      ? selectedChallan.hub.join(", ") || "-"
                      : selectedChallan?.hub || "-"}
                  </p>
                </div>

                <div className="col-md-4">
                  <label className="fw-bold">Chassis Number</label>
                  <p>{selectedChallan?.chassisnumber}</p>
                </div>

                <div className="col-md-4">
                  <label className="fw-bold">Status</label>

                  <p>
                    <span className="badge bg-success">
                      {selectedChallan?.challanStatus}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Challan Cards */}

          <div className="row">
            {selectedChallan?.challans?.map((challan, index) => (
              <div className="col-md-6 mb-4" key={index}>
                <div
                  className="card shadow h-100 border-0"
                  style={{
                    borderLeft:
                      challan.challanStatus === "pending"
                        ? "6px solid #dc3545"
                        : "6px solid #198754",
                    borderRadius: "12px",
                    transition: "0.3s",
                  }}
                >
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{challan.challanNumber}</strong>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6c757d",
                        }}
                      >
                        {formatDate(challan.challanDate)} |{" "}
                        {challan.challanTime}{" "}
                      </div>
                    </div>

                    <span
                      className={`badge px-3 py-2 ${
                        challan.challanStatus === "pending"
                          ? "bg-danger"
                          : "bg-success"
                      }`}
                    >
                      {challan.challanStatus.toUpperCase()}
                    </span>
                  </div>

                  <div className="card-body">
                    {/* Amount */}

                    <div
                      className="text-center mb-3 p-3 rounded"
                      style={{
                        background: "#fff8e1",
                        border: "1px solid #ffe082",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#856404",
                        }}
                      >
                        Challan Amount
                      </div>

                      <h3
                        className="fw-bold mb-0"
                        style={{
                          color: "#ff6f00",
                        }}
                      >
                        ₹{Number(challan.amount).toLocaleString("en-IN")}
                      </h3>
                    </div>

                    {/* Location */}

                    <div className="mb-3">
                      <div className="fw-bold text-primary mb-1">
                        📍 Location
                      </div>

                      {/* Hub */}
                      <div className="mb-3">
                        <div className="fw-bold text-info mb-1">🏢 Hub</div>
                        <div className="text-muted">
                          {formatHub(challan.hub)}
                        </div>
                      </div>
                    </div>

                    {/* Offence */}

                    <div className="mb-3">
                      <div className="fw-bold text-danger mb-1">⚠ Offence</div>

                      <div className="text-muted">{challan.offenseDetails}</div>
                    </div>

                    {/* Court */}

                    <div className="mb-3">
                      <div className="fw-bold text-success mb-1">
                        ⚖ Court Status
                      </div>

                      <div className="text-muted">{challan.courtStatus}</div>
                    </div>

                    <div className="mb-3">
                      <div className="fw-bold text-success mb-1">
                        State | RTO
                      </div>

                      <div className="text-muted">
                        {challan.state} | {challan.rto}{" "}
                      </div>
                    </div>

                    {/* Action Button */}

                    {/* {challan.paymentUrl && (
                      <a
                        href={challan.paymentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-primary w-100 fw-bold shadow"
                      >
                        Pay Challan
                      </a>
                    )} */}
                    {/* Driver Assignment */}

                    {/* Primary Driver */}

                    <div
                      className="mb-3 p-3 rounded"
                      style={{
                        background: "#eef8f1",
                        border: "1px solid #b7e4c7",
                      }}
                    >
                      <div className="fw-bold text-success mb-2">
                        👨‍✈️ Primary Driver
                      </div>

                      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                        {" "}
                        <div>
                          <div className="fw-semibold fs-6">
                            {challan.driverAssignment?.primaryDriver
                              ?.driverName || "Not Assigned"}
                          </div>

                          <div className="small text-muted mt-1">
                            Employee ID:{" "}
                            {challan.driverAssignment?.primaryDriver
                              ?.employeeId || "-"}
                          </div>

                          {challan.driverAssignment?.primaryDriver
                            ?.phoneNumber && (
                            <small className="text-muted">
                              📞{" "}
                              {
                                challan.driverAssignment.primaryDriver
                                  .phoneNumber
                              }
                            </small>
                          )}
                        </div>
                        <div>
                          <div className="fw-bold text-primary mb-2">
                            Actual Driver
                          </div>

                          <div className="fw-semibold">
                            {challan.actualDriver?.drivername ||
                              challan.actualDriver?.id_user?.name ||
                              (challan.actualDriver?._id ===
                              challan.driverAssignment?.primaryDriver?.driverId
                                ? challan.driverAssignment?.primaryDriver
                                    ?.driverName
                                : null) ||
                              "Not Assigned"}
                          </div>

                          <div className="small text-muted mt-1">
                            Phone:{" "}
                            {challan.actualDriver?.phonenumber ||
                              challan.actualDriver?.id_user?.phonenumber ||
                              (challan.actualDriver?._id ===
                              challan.driverAssignment?.primaryDriver?.driverId
                                ? challan.driverAssignment?.primaryDriver
                                    ?.phoneNumber
                                : null) ||
                              "-"}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => {
                            setEditingDriver({
                              vehicleId: selectedChallan.vehicleId,
                              challanNumber: challan.challanNumber,
                            });

                            setSelectedActualDriverId(
                              challan.actualDriver?._id ||
                                challan.driverAssignment?.primaryDriver
                                  ?.driverId ||
                                "",
                            );
                          }}
                        >
                          Edit Actual Driver
                        </button>
                        <span className="badge bg-success">
                          {challan.driverAssignment?.assignmentType?.replaceAll(
                            "_",
                            " ",
                          ) || "NONE"}
                        </span>
                      </div>
                    </div>

                    {/* Drivers On Challan Date */}

                    <div
                      className="mb-3 p-3 rounded"
                      style={{
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                      }}
                    >
                      <div className="fw-bold text-dark mb-2">
                        👥 Drivers On Challan Date
                      </div>

                      {challan.driverAssignment?.allDrivers?.length ? (
                        <div className="d-flex flex-wrap gap-2">
                          {challan.driverAssignment.allDrivers.map((driver) => {
                            const isPrimary =
                              driver.driverId ===
                              challan.driverAssignment?.primaryDriver?.driverId;

                            return (
                              <div
                                key={driver.driverId}
                                className={`rounded px-3 py-2 ${
                                  isPrimary
                                    ? "bg-success text-white"
                                    : "bg-light text-dark border"
                                }`}
                              >
                                <div className="fw-semibold">
                                  {driver.driverName || "-"}
                                </div>
                                <div className="small">
                                  Employee ID: {driver.employeeId || "-"}
                                </div>
                                {isPrimary && (
                                  <span className="badge bg-white text-success mt-1">
                                    Primary
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-muted">
                          No driver records found
                        </div>
                      )}
                    </div>

                    {/* Action Button */}

                    {challan.paymentUrl && (
                      <a
                        href={challan.paymentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-primary w-100 fw-bold shadow"
                      >
                        Pay Challan
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={Boolean(editingDriver)}
        onHide={() => setEditingDriver(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Actual Driver</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <label className="form-label fw-semibold">Select Driver</label>

          <select
            className="form-select"
            value={selectedActualDriverId}
            onChange={(event) => setSelectedActualDriverId(event.target.value)}
          >
            <option value="">Select a driver</option>

            {drivers.map((driver) => {
              const driverName =
                driver.drivername || driver.id_user?.name || "Unnamed Driver";

              const phoneNumber =
                driver.phonenumber || driver.id_user?.phonenumber;

              return (
                <option key={driver._id} value={driver._id}>
                  {driverName}
                  {phoneNumber ? ` — ${phoneNumber}` : ""}
                </option>
              );
            })}
          </select>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setEditingDriver(null)}
            disabled={savingActualDriver}
          >
            Cancel
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={saveActualDriver}
            disabled={savingActualDriver || !selectedActualDriverId}
          >
            {savingActualDriver ? "Saving..." : "Save Driver"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
